/**
 * Notices (1.I-1 deliverable 11): `useNotify()` shows the kit's `Toast` (role `status`),
 * dismissible, closing after 8 s, bottom right of the token root. Texts are fixed copy, never
 * document content.
 */
import { Toast } from '@codex/ui';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { UndoToastHost } from '../undo/undo-toast.js';

export const NOTICE_MS = 8_000;

interface Notice {
  readonly id: number;
  readonly text: string;
}

const NoticeContext = createContext<(text: string) => void>(() => {});

export function useNotify(): (text: string) => void {
  return useContext(NoticeContext);
}

export function NoticeProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<readonly Notice[]>([]);
  const next = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) clearTimeout(timer);
    timers.current.delete(id);
    setItems((all) => all.filter((item) => item.id !== id));
  }, []);

  const notify = useCallback(
    (text: string) => {
      const id = next.current++;
      setItems((all) => [...all, { id, text }]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), NOTICE_MS),
      );
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  return (
    <NoticeContext.Provider value={notify}>
      {children}
      <div className="cx-shell-notices">
        {items.map((item) => (
          <Toast key={item.id} onDismiss={() => dismiss(item.id)}>
            {item.text}
          </Toast>
        ))}
        <UndoToastHost notify={notify} />
      </div>
    </NoticeContext.Provider>
  );
}
